import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Stack,
  Rating,
  Slider,
  Snackbar,
  FormHelperText
} from "@mui/material";
import {
  FolderKanban,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Calendar,
  ArrowUpRight,
  TrendingUp,
  Filter,
  RefreshCw,
  Plus,
  Rocket,
  Edit3,
  Trash2,
  Eye,
  Briefcase,
  DollarSign,
  Layers,
  Award,
  Users,
  ShieldAlert,
  Sparkles,
  X
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const NpiIntakeDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  // State
  const [loading, setLoading] = useState(true);
  const [intakes, setIntakes] = useState([]);
  const [metrics, setMetrics] = useState({
    total: 0,
    submitted: 0,
    underReview: 0,
    feasibilityCheck: 0,
    approved: 0,
    converted: 0,
    rejected: 0,
    onHold: 0,
    totalPipelineBudget: 0
  });

  // Filter States
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // Available Data for Dropdowns
  const [employees, setEmployees] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
  const [usersMap, setUsersMap] = useState({});

  const userRole = (localStorage.getItem("role") || "").toLowerCase();
  const isProjectManager = userRole === "project-manager" || userRole === "manager";
  const isProductOwner = userRole === "product-owner";
  const isProductManager = userRole === "product-manager";

  const getEmployeeRole = (emp) => {
    const emailKey = (emp.companyemail || emp.personalemail || emp.email || "").toLowerCase();
    return emp.role || emp.designation || usersMap[emailKey] || "";
  };

  const currentEmpObj = employees.find(e => String(e.employee_id || e.id) === String(currentEmployeeId));
  const currentEmpName = currentEmpObj 
    ? `${currentEmpObj.firstName || ''} ${currentEmpObj.lastName || ''}`.trim() 
    : (localStorage.getItem("userName") || localStorage.getItem("user") || "Logged-in User");

  const projectManagersList = employees.filter(emp => {
    const r = getEmployeeRole(emp).toLowerCase();
    return r === "project-manager" || r === "project manager" || r === "manager";
  });

  const productManagersList = employees.filter(emp => {
    const r = getEmployeeRole(emp).toLowerCase();
    return r === "product-manager" || r === "product manager";
  });

  const productOwnersList = employees.filter(emp => {
    const r = getEmployeeRole(emp).toLowerCase();
    return r === "product-owner" || r === "product owner";
  });

  // Dialog States
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusReasonError, setStatusReasonError] = useState("");
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedIntake, setSelectedIntake] = useState(null);

  // AI Recommendation State
  const [individualModalOpen, setIndividualModalOpen] = useState(false);
  const [individualAiLoading, setIndividualAiLoading] = useState(false);
  const [individualAiData, setIndividualAiData] = useState(null);
  const [individualAiError, setIndividualAiError] = useState(null);
  const [npiAiTitle, setNpiAiTitle] = useState("");
  const [selectedNpiForAI, setSelectedNpiForAI] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);

  // Form State: Create Intake
  const [createForm, setCreateForm] = useState({
    title: "",
    category: "Software",
    business_case: "",
    priority: "Medium",
    proposed_start_date: "",
    proposed_end_date: "",
    estimated_budget: "",
    strategic_alignment_score: 7,
    feasibility_score: 7,
    risk_level: "Low",
    requested_skills: [{ role: "Full Stack Developer", count: 2, skill: "React, Node.js" }]
  });

  // Form State: Full Edit Intake Proposal
  const [editForm, setEditForm] = useState({
    title: "",
    category: "Software",
    business_case: "",
    priority: "Medium",
    proposed_start_date: "",
    proposed_end_date: "",
    estimated_budget: "",
    strategic_alignment_score: 5,
    feasibility_score: 5,
    risk_level: "Low",
    status: "Submitted",
    status_change_reason: "",
    review_notes: "",
    requested_skills: [{ role: "Full Stack Developer", count: 1, skill: "React, Node.js" }]
  });

  // Form State: Review & Evaluate
  const [reviewForm, setReviewForm] = useState({
    status: "Under_Review",
    status_change_reason: "",
    strategic_alignment_score: 5,
    feasibility_score: 5,
    risk_level: "Low",
    review_notes: "",
    rejection_reason: ""
  });

  // Form State: Convert to Project
  const [convertForm, setConvertForm] = useState({
    name: "",
    key: "",
    projectType: "Software",
    template: "Scrum",
    manager_id: "",
    product_manager_id: "",
    product_owner_id: "",
    lead_id: "",
    costCenterId: "",
    start_date: "",
    end_date: "",
    budget: "",
    description: ""
  });

  // Notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [actionLoading, setActionLoading] = useState(false);

  const showNotification = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Auth Header Helper
const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${token}`
  },
  withCredentials: true
});

  // 1. Load Intakes & Supporting Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/npi`, {
        ...getAuthHeaders(),
        params: {
          status: activeTab !== "All" ? activeTab : undefined,
          priority: priorityFilter !== "All" ? priorityFilter : undefined,
          category: categoryFilter !== "All" ? categoryFilter : undefined,
          search: searchQuery || undefined
        }
      });

      if (res.data?.status === "success") {
        setIntakes(res.data.data || []);
        if (res.data.metrics) {
          setMetrics(res.data.metrics);
        }
      }
    } catch (err) {
      //console.error("Error fetching NPI intakes:", err);
      showNotification(err.response?.data?.message || "Failed to fetch NPI intakes", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedExtensions = /(\.xlsx|\.xls)$/i;
    if (!allowedExtensions.exec(file.name)) {
      showNotification("Only Excel files (.xlsx, .xls) are allowed!", "error");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setActionLoading(true);
      showNotification("Uploading NPI proposals...", "info");

      const response = await axios.post(`${API_URL}/api/npi/bulk-upload`, formData, {
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data"
        },
        withCredentials:true
      });

      if (response.data?.status === "success") {
        showNotification(response.data.message || "Proposals uploaded successfully!", "success");
        fetchData();
      } else {
        throw new Error(response.data?.message || "Bulk upload failed.");
      }
    } catch (err) {
      //console.error("Excel upload error:", err);
      const errMsg = err.response?.data?.message || err.message || "Failed to upload Excel sheet.";
      showNotification(errMsg, "error");
    } finally {
      setActionLoading(false);
      e.target.value = "";
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setActionLoading(true);
      showNotification("Downloading Excel template...", "info");

      const response = await axios.get(`${API_URL}/api/npi/download-template`, {
        headers: getAuthHeaders().headers,
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "NPI_Proposals_Template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showNotification("Template downloaded successfully!", "success");
    } catch (err) {
      //console.error("Error downloading template:", err);
      showNotification("Failed to download template.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Helper to fetch the exact logged-in employee_id directly from the server session
  const getEmployeeid = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return null;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        throw new Error('Employee ID not found in user data');
      }

      setCurrentEmployeeId(response.data.employee_id);
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      return currentEmployeeId || null;
    }
  };

  // 2. Load Employees, Cost Centers & User Roles once
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        let company_id = null;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          company_id = payload.company_id;
        } catch (e) {}

        const promises = [
          getEmployeeid(),
          axios.get(`${API_URL}/api/viewpersonal?limit=1000`, getAuthHeaders()).catch(() => null),
          axios.get(`${API_URL}/api/addteam/employees`, getAuthHeaders()).catch(() => null),
          axios.get(`${API_URL}/api/user/a/b/c`, getAuthHeaders()).catch(() => null),
          axios.get(`${API_URL}/api/cost-centers`, getAuthHeaders()).catch(() => null)
        ];

        if (company_id) {
          promises.push(axios.get(`${API_URL}/api/cmp/${company_id}`, getAuthHeaders()).catch(() => null));
        }

        const results = await Promise.all(promises);
        const empIdRes = results[0];
        const vpRes = results[1];
        const empRes = results[2];
        const userRolesRes = results[3];
        const ccRes = results[4];
        const cmpRes = company_id ? results[5] : null;

        if (cmpRes?.data?.data) {
          setCompanyConfig(cmpRes.data.data);
        }

        // Roles map from user service
        if (userRolesRes?.data?.data && Array.isArray(userRolesRes.data.data)) {
          const map = {};
          userRolesRes.data.data.forEach(u => {
            if (u.email) map[u.email.toLowerCase()] = u.role;
          });
          setUsersMap(map);
        }

        // Prefer /api/viewpersonal as it has complete details matching createproject.jsx
        if (vpRes?.data?.data && Array.isArray(vpRes.data.data) && vpRes.data.data.length > 0) {
          setEmployees(vpRes.data.data);
        } else if (empRes?.data && Array.isArray(empRes.data)) {
          setEmployees(empRes.data);
        }

        // Cost Centers
        if (ccRes?.data && Array.isArray(ccRes.data)) {
          setCostCenters(ccRes.data);
        } else if (ccRes?.data?.data && Array.isArray(ccRes.data.data)) {
          setCostCenters(ccRes.data.data);
        }
      } catch (e) {
        //console.error("Error loading dropdown metadata:", e);
      }
    };
    loadMetadata();
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab, priorityFilter, categoryFilter]);

  // Deep-link Auto Open: When redirected from CPO Dashboard with an openId or state
  const handledDeepLinkRef = useRef(null);

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    if (location.search || location.state?.openIntakeId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetIntakeId = location.state?.openIntakeId || params.get("openId");
    if (!targetIntakeId || handledDeepLinkRef.current === String(targetIntakeId)) return;

    const openProposal = async () => {
      handledDeepLinkRef.current = String(targetIntakeId);
      // Clean query params & navigation state so subsequent tab clicks / re-renders don't re-trigger popup
      navigate(location.pathname, { replace: true, state: {} });

      // 1. Check if already loaded in current intakes state
      const found = intakes.find(i => String(i.intake_id) === String(targetIntakeId));
      if (found) {
        setSelectedIntake(found);
        setDetailsModalOpen(true);
        return;
      }
      // 2. Fetch directly by ID if not in current filtered list
      try {
        const res = await axios.get(`${API_URL}/api/npi/${targetIntakeId}`, getAuthHeaders());
        if (res.data?.data) {
          setSelectedIntake(res.data.data);
          setDetailsModalOpen(true);
        }
      } catch (e) {
        //console.error("Could not fetch target intake proposal for deep-link:", e);
      }
    };

    openProposal();
  }, [location.search, location.state, intakes.length]);

  // Handle Search Debounce
  const handleSearchSubmit = (e) => {
    if (e.key === "Enter") {
      fetchData();
    }
  };

  // Helpers for Project Key Generation
  const generateSuggestedKey = (title) => {
    if (!title) return "PRJ-01";
    const cleaned = title.replace(/[^a-zA-Z0-9 ]/g, "").trim();
    const words = cleaned.split(/\s+/);
    let initials = "";
    if (words.length === 1) {
      initials = words[0].substring(0, 3).toUpperCase();
    } else {
      initials = words.slice(0, 3).map(w => w[0].toUpperCase()).join("");
    }
    const randomNum = Math.floor(100 + Math.random() * 900);
    return `${initials || "PRJ"}-${randomNum}`;
  };

  // Open Create Modal
  const handleOpenCreateModal = async () => {
    await getEmployeeid();
    setCreateForm({
      title: "",
      category: "Software",
      business_case: "",
      priority: "Medium",
      proposed_start_date: new Date().toISOString().split("T")[0],
      proposed_end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      estimated_budget: "",
      strategic_alignment_score: 7,
      feasibility_score: 7,
      risk_level: "Low",
      requested_skills: [{ role: "Full Stack Developer", count: 2, skill: "React, Node.js" }]
    });
    setCreateModalOpen(true);
  };

  // Submit Create Intake
  const handleSubmitCreate = async () => {
    if (!createForm.title.trim()) {
      showNotification("Please enter a Proposal Title", "warning");
      return;
    }

    try {
      setActionLoading(true);
      // Fetch the exact logged-in employee_id directly from the server session
      const activeEmployeeId = await getEmployeeid();

      const payload = {
        ...createForm,
        submitted_by: activeEmployeeId || currentEmployeeId
      };
      const res = await axios.post(`${API_URL}/api/npi`, payload, getAuthHeaders());
      if (res.data?.status === "success") {
        showNotification("Intake proposal submitted successfully!");
        setCreateModalOpen(false);
        fetchData();
      }
    } catch (err) {
      //console.error("Error submitting intake:", err);
      showNotification(err.response?.data?.message || "Failed to submit proposal", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Reusable Interactive Strategic & Feasibility Progress Bars
  const renderScoreMeters = (formData, setFormData) => {
    const stratScore = Number(formData.strategic_alignment_score) || 5;
    const feasScore = Number(formData.feasibility_score) || 5;

    const getStratMeta = (score) => {
      if (score >= 8) return { text: "High Strategic Alignment", color: "#4F46E5", bg: "#EEF2FF" };
      if (score >= 5) return { text: "Moderate Strategic Fit", color: "#2563EB", bg: "#EFF6FF" };
      return { text: "Low Strategic Alignment", color: "#D97706", bg: "#FEF3C7" };
    };

    const getFeasMeta = (score) => {
      if (score >= 8) return { text: "Optimal Capacity & Feasibility", color: "#059669", bg: "#ECFDF5" };
      if (score >= 5) return { text: "Feasible / Minor Resource Needs", color: "#0D9488", bg: "#F0FDFA" };
      return { text: "High Technical Complexity / Risk", color: "#DC2626", bg: "#FEF2F2" };
    };

    const stratMeta = getStratMeta(stratScore);
    const feasMeta = getFeasMeta(feasScore);

    return (
      <Box sx={{ p: 2.5, bgcolor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 2.5, my: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} color="#1E293B" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <TrendingUp size={18} color="#4F46E5" /> Strategic & Technical Evaluation Meter
        </Typography>

        <Grid container spacing={2.5}>
          {/* Strategic Alignment Progress Bar */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: "#FFFFFF", borderRadius: 2, border: "1px solid #E2E8F0", height: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TrendingUp size={16} color="#4F46E5" />
                  <Typography variant="body2" fontWeight={700} color="#1E293B">
                    Strategic Alignment
                  </Typography>
                </Box>
                <Chip
                  label={`${stratScore}/10 (${stratScore * 10}%)`}
                  size="small"
                  sx={{ bgcolor: stratMeta.bg, color: stratMeta.color, fontWeight: 700, fontSize: "0.75rem" }}
                />
              </Box>

              {/* Progress Bar Track */}
              <LinearProgress
                variant="determinate"
                value={stratScore * 10}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: "#EEF2FF",
                  mb: 1.5,
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 5,
                    background: "linear-gradient(90deg, #818CF8 0%, #4F46E5 100%)"
                  }
                }}
              />

              {/* Interactive Slider */}
              <Slider
                value={stratScore}
                min={1}
                max={10}
                step={1}
                marks={[
                  { value: 1, label: "1" },
                  { value: 3, label: "3" },
                  { value: 5, label: "5" },
                  { value: 8, label: "8" },
                  { value: 10, label: "10" }
                ]}
                onChange={(e, val) => setFormData(prev => ({ ...prev, strategic_alignment_score: val }))}
                sx={{
                  color: "#4F46E5",
                  py: 1,
                  "& .MuiSlider-thumb": {
                    width: 18,
                    height: 18,
                    boxShadow: "0 2px 6px rgba(79, 70, 229, 0.4)"
                  }
                }}
              />

              <Typography variant="caption" sx={{ color: stratMeta.color, fontWeight: 600, display: "block", mt: 0.5 }}>
                • {stratMeta.text}
              </Typography>
            </Box>
          </Grid>

          {/* Technical Feasibility Progress Bar */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: "#FFFFFF", borderRadius: 2, border: "1px solid #E2E8F0", height: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Sparkles size={16} color="#059669" />
                  <Typography variant="body2" fontWeight={700} color="#1E293B">
                    Technical Feasibility
                  </Typography>
                </Box>
                <Chip
                  label={`${feasScore}/10 (${feasScore * 10}%)`}
                  size="small"
                  sx={{ bgcolor: feasMeta.bg, color: feasMeta.color, fontWeight: 700, fontSize: "0.75rem" }}
                />
              </Box>

              {/* Progress Bar Track */}
              <LinearProgress
                variant="determinate"
                value={feasScore * 10}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: "#ECFDF5",
                  mb: 1.5,
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 5,
                    background: "linear-gradient(90deg, #34D399 0%, #059669 100%)"
                  }
                }}
              />

              {/* Interactive Slider */}
              <Slider
                value={feasScore}
                min={1}
                max={10}
                step={1}
                marks={[
                  { value: 1, label: "1" },
                  { value: 3, label: "3" },
                  { value: 5, label: "5" },
                  { value: 8, label: "8" },
                  { value: 10, label: "10" }
                ]}
                onChange={(e, val) => setFormData(prev => ({ ...prev, feasibility_score: val }))}
                sx={{
                  color: "#059669",
                  py: 1,
                  "& .MuiSlider-thumb": {
                    width: 18,
                    height: 18,
                    boxShadow: "0 2px 6px rgba(5, 150, 105, 0.4)"
                  }
                }}
              />

              <Typography variant="caption" sx={{ color: feasMeta.color, fontWeight: 600, display: "block", mt: 0.5 }}>
                • {feasMeta.text}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Open Full Edit Modal
  const handleOpenEditModal = (intake) => {
    setSelectedIntake(intake);
    setStatusReasonError("");

    let skills = [{ role: "Full Stack Developer", count: 1, skill: "React, Node.js" }];
    if (intake.requested_skills) {
      try {
        const parsed = typeof intake.requested_skills === "string" 
          ? JSON.parse(intake.requested_skills) 
          : intake.requested_skills;
        if (Array.isArray(parsed) && parsed.length > 0) {
          skills = parsed;
        }
      } catch (e) {
        skills = [{ role: "Resource Demands", count: 1, skill: String(intake.requested_skills) }];
      }
    }

    setEditForm({
      title: intake.title || "",
      category: intake.category || "Software",
      business_case: intake.business_case || "",
      priority: intake.priority || "Medium",
      proposed_start_date: intake.proposed_start_date || "",
      proposed_end_date: intake.proposed_end_date || "",
      estimated_budget: intake.estimated_budget || "",
      strategic_alignment_score: intake.strategic_alignment_score || 5,
      feasibility_score: intake.feasibility_score || 5,
      risk_level: intake.risk_level || "Low",
      status: intake.status || "Submitted",
      status_change_reason: intake.status_change_reason || "",
      review_notes: intake.review_notes || "",
      requested_skills: skills
    });
    setEditModalOpen(true);
  };

  // Submit Full Edit Update
  const handleSubmitEdit = async () => {
    if (!selectedIntake) return;

    if (!editForm.title.trim()) {
      showNotification("Proposal Title is required", "warning");
      return;
    }

    // MANDATORY REASON CHECK: If status is updated
    const statusChanged = editForm.status !== selectedIntake.status;
    if (statusChanged && (!editForm.status_change_reason || !editForm.status_change_reason.trim())) {
      setStatusReasonError("A mandatory reason must be provided when changing proposal status.");
      showNotification("Reason is mandatory when updating status!", "error");
      return;
    }

    try {
      setActionLoading(true);
      const payload = {
        ...editForm,
        reviewer_id: currentEmployeeId
      };
      const res = await axios.put(`${API_URL}/api/npi/${selectedIntake.intake_id}`, payload, getAuthHeaders());
      if (res.data?.status === "success") {
        showNotification("Proposal updated successfully!");
        setEditModalOpen(false);
        fetchData();
      }
    } catch (err) {
      //console.error("Error updating proposal:", err);
      showNotification(err.response?.data?.message || "Failed to update proposal", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Skill row handlers for edit form
  const handleEditAddSkillRow = () => {
    setEditForm(prev => ({
      ...prev,
      requested_skills: [...prev.requested_skills, { role: "", count: 1, skill: "" }]
    }));
  };

  const handleEditSkillChange = (index, field, value) => {
    const updated = [...editForm.requested_skills];
    updated[index][field] = value;
    setEditForm(prev => ({ ...prev, requested_skills: updated }));
  };

  const handleEditRemoveSkillRow = (index) => {
    setEditForm(prev => ({
      ...prev,
      requested_skills: prev.requested_skills.filter((_, i) => i !== index)
    }));
  };

  // Compatibility helper: forward review modal calls to full edit modal
  const handleOpenReviewModal = (intake) => {
    handleOpenEditModal(intake);
  };

  // Open Convert to Project Modal
  const handleOpenConvertModal = (intake) => {
    setSelectedIntake(intake);

    // Auto-fill leadership assignments based on logged-in role
    let managerId = "";
    let productManagerId = "";
    let productOwnerId = "";

    if (isProjectManager) {
      managerId = currentEmployeeId || "";
    } else if (projectManagersList.length > 0) {
      managerId = projectManagersList[0].employee_id || projectManagersList[0].id || "";
    }

    if (isProductOwner) {
      productOwnerId = currentEmployeeId || "";
    }

    if (isProductManager) {
      productManagerId = currentEmployeeId || "";
    }

    setConvertForm({
      name: intake.title || "",
      key: generateSuggestedKey(intake.title),
      projectType: intake.category || "Software",
      template: "Scrum",
      manager_id: managerId,
      product_manager_id: productManagerId,
      product_owner_id: productOwnerId,
      lead_id: "",
      costCenterId: costCenters[0]?.id || "",
      start_date: intake.proposed_start_date || new Date().toISOString().split("T")[0],
      end_date: intake.proposed_end_date || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      budget: intake.estimated_budget || "",
      description: intake.business_case || ""
    });
    setConvertModalOpen(true);
  };

  // Submit Conversion to Active Project
  const handleSubmitConvert = async () => {
    if (!selectedIntake) return;
    if (!convertForm.name.trim()) {
      showNotification("Project Name is required", "warning");
      return;
    }
    if (!convertForm.key.trim()) {
      showNotification("Project Key is required", "warning");
      return;
    }
    if (!convertForm.manager_id) {
      showNotification("Please select a Project Manager", "warning");
      return;
    }

    try {
      setActionLoading(true);
      const res = await axios.post(
        `${API_URL}/api/npi/${selectedIntake.intake_id}/convert`,
        convertForm,
        getAuthHeaders()
      );

      if (res.data?.status === "success") {
        showNotification("Project successfully created & launched from NPI Intake!");
        setConvertModalOpen(false);
        fetchData();
      }
    } catch (err) {
      //console.error("Error converting intake:", err);
      showNotification(err.response?.data?.message || "Failed to convert intake to project", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Delete Intake
  const handleDeleteIntake = async (intakeId) => {
    if (!window.confirm("Are you sure you want to delete this intake proposal?")) return;
    try {
      const res = await axios.delete(`${API_URL}/api/npi/${intakeId}`, getAuthHeaders());
      if (res.data?.status === "success") {
        showNotification("Intake proposal deleted.");
        fetchData();
      }
    } catch (err) {
      //console.error("Error deleting intake:", err);
      showNotification(err.response?.data?.message || "Failed to delete proposal", "error");
    }
  };

  // Skill row add/remove handlers
  const handleAddSkillRow = () => {
    setCreateForm(prev => ({
      ...prev,
      requested_skills: [...prev.requested_skills, { role: "", count: 1, skill: "" }]
    }));
  };

  const handleSkillChange = (index, field, value) => {
    const updated = [...createForm.requested_skills];
    updated[index][field] = value;
    setCreateForm(prev => ({ ...prev, requested_skills: updated }));
  };

  const handleRemoveSkillRow = (index) => {
    setCreateForm(prev => ({
      ...prev,
      requested_skills: prev.requested_skills.filter((_, i) => i !== index)
    }));
  };

  // Trigger AI Recommendation for an individual intake proposal
  const handleTriggerIndividualAI = async (npiId, title) => {
    try {
      setSelectedNpiForAI(npiId);
      setNpiAiTitle(title);
      setIndividualAiLoading(true);
      setIndividualAiError(null);
      setIndividualModalOpen(true);
      setIndividualAiData(null);
      const res = await axios.get(`${API_URL}/api/npi/${npiId}/ai-recommendations`, getAuthHeaders());
      if (res.data?.status === "success") {
        setIndividualAiData(res.data.data);
      } else {
        throw new Error(res.data?.message || "Failed to fetch AI resource recommendations.");
      }
    } catch (err) {
      //console.error("AI Recommendation error:", err);
      const errorData = err.response?.data;
      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setIndividualAiError({
          type: "upgrade",
          message: errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.",
          severity: "warning"
        });
        showNotification(errorData.message || "AI features are not included in your current plan. Please upgrade to unlock AI.", "warning");
        window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI NPI Resource Matching" } }));
      } else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setIndividualAiError({
          type: "exhausted",
          message: `⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`,
          severity: "warning"
        });
        showNotification(`⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`, "warning");
      } else {
        setIndividualAiError({
          type: "error",
          message: errorData?.message || err.message || "Failed to get AI recommendations",
          severity: "error"
        });
        showNotification(errorData?.message || err.message || "Failed to get AI recommendations", "error");
      }
    } finally {
      setIndividualAiLoading(false);
    }
  };

  // Helper for Status Badge Color
  const getStatusChip = (status) => {
    switch (status) {
      case "Submitted":
        return <Chip label="Submitted" size="small" sx={{ bgcolor: "#E0F2FE", color: "#0369A1", fontWeight: 600 }} />;
      case "Under_Review":
        return <Chip label="Under Review" size="small" sx={{ bgcolor: "#FEF3C7", color: "#B45309", fontWeight: 600 }} />;
      case "Feasibility_Check":
        return <Chip label="Feasibility Check" size="small" sx={{ bgcolor: "#EDE9FE", color: "#6D28D9", fontWeight: 600 }} />;
      case "Approved":
        return <Chip label="Approved" size="small" sx={{ bgcolor: "#DCFCE7", color: "#15803D", fontWeight: 600 }} />;
      case "Converted":
        return <Chip label="Converted to Project" size="small" sx={{ bgcolor: "#DBEAFE", color: "#1D4ED8", fontWeight: 700 }} />;
      case "On_Hold":
        return <Chip label="On Hold" size="small" sx={{ bgcolor: "#F3F4F6", color: "#4B5563", fontWeight: 600 }} />;
      case "Rejected":
        return <Chip label="Rejected" size="small" sx={{ bgcolor: "#FEE2E2", color: "#B91C1C", fontWeight: 600 }} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // Priority Chip Helper
  const getPriorityChip = (priority) => {
    switch (priority) {
      case "Critical":
        return <Chip label="Critical" size="small" sx={{ bgcolor: "#FFE4E6", color: "#E11D48", fontWeight: 600 }} />;
      case "High":
        return <Chip label="High" size="small" sx={{ bgcolor: "#FFEDD5", color: "#C2410C", fontWeight: 600 }} />;
      case "Medium":
        return <Chip label="Medium" size="small" sx={{ bgcolor: "#FEF9C3", color: "#A16207", fontWeight: 600 }} />;
      case "Low":
        return <Chip label="Low" size="small" sx={{ bgcolor: "#F1F5F9", color: "#475569", fontWeight: 600 }} />;
      default:
        return <Chip label={priority} size="small" />;
    }
  };

  // Helper to format business case text into readable bullet points
  const formatBusinessCaseToBullets = (text) => {
    if (!text || !text.trim()) {
      return ["No detailed business case provided."];
    }

    // Split by newlines first
    let rawLines = text
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    // If only one line, check if sentences or bullet markers can be split
    if (rawLines.length === 1) {
      if (/[\u2022\u25cf\*\-]|\d+\.\s/.test(rawLines[0])) {
        rawLines = rawLines[0]
          .split(/(?:\s*[\u2022\u25cf\*\-]\s*|\s*\d+\.\s+)/)
          .map(l => l.trim())
          .filter(Boolean);
      } else if (rawLines[0].includes(". ")) {
        rawLines = rawLines[0]
          .split(/(?<=[.!?])\s+/)
          .map(l => l.trim())
          .filter(Boolean);
      }
    }

    // Clean leading bullets, dashes, numbers from lines
    const cleaned = rawLines
      .map(line => line.replace(/^[\u2022\u25cf\*\-\s\d.)]+/, "").trim())
      .filter(line => line.length > 0);

    return cleaned.length > 0 ? cleaned : [text];
  };

  return (
    <Box sx={{ p: 4, minHeight: "100vh", bgcolor: "#F8FAFC" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          
            <Typography variant="h4" fontWeight={700} color="#0F172A">
              NPI & Project Intake
            </Typography>
          </Box>
          <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>
            Evaluate incoming project proposals, assess feasibility, score business impact, and seamlessly convert approved ideas into active projects.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<RefreshCw size={16} />}
            onClick={fetchData}
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#CBD5E1", color: "#334155" }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownloadTemplate}
            startIcon={<ArrowUpRight size={16} />}
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#CBD5E1", color: "#334155" }}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={<ArrowUpRight size={16} />}
            sx={{ textTransform: "none", borderRadius: 2, borderColor: "#CBD5E1", color: "#334155" }}
          >
            Excel Upload
            <input
              type="file"
              hidden
              accept=".xlsx, .xls"
              onChange={handleExcelUpload}
            />
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleOpenCreateModal}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: "#4F46E5",
              "&:hover": { bgcolor: "#4338CA" },
              boxShadow: "0 4px 12px rgba(79, 70, 229, 0.25)"
            }}
          >
            New Intake
          </Button>
        </Stack>
      </Box>

      {/* KPI Metric Cards (Clickable to switch tab) */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {/* Total Proposals -> All */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            onClick={() => setActiveTab("All")}
            sx={{
              borderRadius: 3,
              border: activeTab === "All" ? "2px solid #4F46E5" : "1px solid #E2E8F0",
              boxShadow: activeTab === "All" ? "0 6px 16px rgba(79, 70, 229, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 6px 16px rgba(79, 70, 229, 0.15)",
                borderColor: "#4F46E5"
              }
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" fontWeight={700} color={activeTab === "All" ? "#4F46E5" : "#64748B"} textTransform="uppercase">
                  Total Proposals
                </Typography>
                <Box sx={{ p: 0.75, bgcolor: "#EEF2FF", color: "#4F46E5", borderRadius: 1.5 }}>
                  <FolderKanban size={18} />
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700} color="#0F172A" sx={{ mt: 1 }}>
                {metrics.total}
              </Typography>
              <Typography variant="caption" color="#64748B">
                Pipeline Value: {companyConfig?.currency_symbol || '₹'}{(metrics.totalPipelineBudget || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Under Review */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            onClick={() => setActiveTab("Under_Review")}
            sx={{
              borderRadius: 3,
              border: activeTab === "Under_Review" ? "2px solid #D97706" : "1px solid #E2E8F0",
              boxShadow: activeTab === "Under_Review" ? "0 6px 16px rgba(217, 119, 6, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 6px 16px rgba(217, 119, 6, 0.15)",
                borderColor: "#D97706"
              }
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" fontWeight={700} color={activeTab === "Under_Review" ? "#D97706" : "#64748B"} textTransform="uppercase">
                  Under Review
                </Typography>
                <Box sx={{ p: 0.75, bgcolor: "#FEF3C7", color: "#D97706", borderRadius: 1.5 }}>
                  <Clock size={18} />
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700} color="#D97706" sx={{ mt: 1 }}>
                {metrics.underReview + metrics.submitted}
              </Typography>
              <Typography variant="caption" color="#64748B">
                Awaiting PM Assessment
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Feasibility Check */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            onClick={() => setActiveTab("Feasibility_Check")}
            sx={{
              borderRadius: 3,
              border: activeTab === "Feasibility_Check" ? "2px solid #7C3AED" : "1px solid #E2E8F0",
              boxShadow: activeTab === "Feasibility_Check" ? "0 6px 16px rgba(124, 58, 237, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 6px 16px rgba(124, 58, 237, 0.15)",
                borderColor: "#7C3AED"
              }
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" fontWeight={700} color={activeTab === "Feasibility_Check" ? "#7C3AED" : "#64748B"} textTransform="uppercase">
                  Feasibility Check
                </Typography>
                <Box sx={{ p: 0.75, bgcolor: "#EDE9FE", color: "#7C3AED", borderRadius: 1.5 }}>
                  <Sparkles size={18} />
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700} color="#7C3AED" sx={{ mt: 1 }}>
                {metrics.feasibilityCheck}
              </Typography>
              <Typography variant="caption" color="#64748B">
                Capacity & Skill Check
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Approved (Ready) */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            onClick={() => setActiveTab("Approved")}
            sx={{
              borderRadius: 3,
              border: activeTab === "Approved" ? "2px solid #16A34A" : "1px solid #E2E8F0",
              boxShadow: activeTab === "Approved" ? "0 6px 16px rgba(22, 163, 74, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 6px 16px rgba(22, 163, 74, 0.15)",
                borderColor: "#16A34A"
              }
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" fontWeight={700} color={activeTab === "Approved" ? "#16A34A" : "#64748B"} textTransform="uppercase">
                  Approved (Ready)
                </Typography>
                <Box sx={{ p: 0.75, bgcolor: "#DCFCE7", color: "#16A34A", borderRadius: 1.5 }}>
                  <CheckCircle2 size={18} />
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700} color="#16A34A" sx={{ mt: 1 }}>
                {metrics.approved}
              </Typography>
              <Typography variant="caption" color="#16A34A" fontWeight={500}>
                Ready to Convert
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Projects -> Converted */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            onClick={() => setActiveTab("Converted")}
            sx={{
              borderRadius: 3,
              border: activeTab === "Converted" ? "2px solid #2563EB" : "1px solid #E2E8F0",
              boxShadow: activeTab === "Converted" ? "0 6px 16px rgba(37, 99, 235, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 6px 16px rgba(37, 99, 235, 0.15)",
                borderColor: "#2563EB"
              }
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="caption" fontWeight={700} color={activeTab === "Converted" ? "#2563EB" : "#64748B"} textTransform="uppercase">
                  Active Projects
                </Typography>
                <Box sx={{ p: 0.75, bgcolor: "#DBEAFE", color: "#2563EB", borderRadius: 1.5 }}>
                  <Rocket size={18} />
                </Box>
              </Box>
              <Typography variant="h4" fontWeight={700} color="#2563EB" sx={{ mt: 1 }}>
                {metrics.converted}
              </Typography>
              <Typography variant="caption" color="#64748B">
                Successfully Launched
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Area */}
      <Paper sx={{ borderRadius: 3, border: "1px solid #E2E8F0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: "1px solid #E2E8F0", bgcolor: "#FFFFFF", px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                py: 2,
                minHeight: "auto",
                color: "#64748B",
                "&.Mui-selected": { color: "#4F46E5" }
              },
              "& .MuiTabs-indicator": { bgcolor: "#4F46E5", height: 3 }
            }}
          >
            <Tab label="All Proposals" value="All" />
            <Tab label="Submitted" value="Submitted" />
            <Tab label="Under Review" value="Under_Review" />
            <Tab label="Feasibility Check" value="Feasibility_Check" />
            <Tab label="Approved" value="Approved" />
            <Tab label="Converted to Project" value="Converted" />
            <Tab label="On Hold" value="On_Hold" />
            <Tab label="Rejected" value="Rejected" />
          </Tabs>
        </Box>

        {/* Filter Controls Bar */}
        <Box sx={{ p: 2, bgcolor: "#FFFFFF", borderBottom: "1px solid #F1F5F9", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search proposal title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchSubmit}
            sx={{ minWidth: 260, bgcolor: "#F8FAFC", "& fieldset": { borderColor: "#E2E8F0" } }}
          />

          <FormControl size="small" sx={{ minWidth: 140, bgcolor: "#F8FAFC" }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="All">All Priorities</MenuItem>
              <MenuItem value="Critical">Critical</MenuItem>
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150, bgcolor: "#F8FAFC" }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="All">All Categories</MenuItem>
              <MenuItem value="Software">Software</MenuItem>
              <MenuItem value="Hardware">Hardware</MenuItem>
              <MenuItem value="Product Feature">Product Feature</MenuItem>
              <MenuItem value="Client Solution">Client Solution</MenuItem>
              <MenuItem value="Internal Tool">Internal Tool</MenuItem>
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="text"
            onClick={() => {
              setSearchQuery("");
              setPriorityFilter("All");
              setCategoryFilter("All");
            }}
            sx={{ color: "#64748B", textTransform: "none" }}
          >
            Clear Filters
          </Button>
        </Box>

        {/* Intakes Table */}
        {loading ? (
          <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={36} sx={{ color: "#4F46E5" }} />
          </Box>
        ) : intakes.length === 0 ? (
          <Box sx={{ p: 8, textAlign: "center" }}>
            <Box sx={{ width: 48, height: 48, mx: "auto", bgcolor: "#EEF2FF", color: "#4F46E5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FolderKanban size={24} />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} color="#1E293B">
              No project intake proposals found
            </Typography>
            <Typography variant="body2" color="#64748B" sx={{ mb: 2 }}>
              Get started by submitting a new initiative for evaluation.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={handleOpenCreateModal}
              sx={{ textTransform: "none", bgcolor: "#4F46E5", borderRadius: 2 }}
            >
              Submit Intake Proposal
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.8125rem" }}>PROPOSAL TITLE & CATEGORY</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.8125rem" }}>PRIORITY</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.8125rem" }}>EST. BUDGET</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.8125rem" }}>PROPOSED DATES</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.8125rem" }}>STRATEGIC / FEASIBILITY</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.8125rem" }}>STATUS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "#475569", fontSize: "0.8125rem" }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {intakes.map((intake) => (
                  <TableRow key={intake.intake_id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="#0F172A">
                          {intake.title}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                          <Chip label={intake.category || "Software"} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                          {intake.risk_level && (
                            <Typography variant="caption" color={intake.risk_level === "High" ? "#DC2626" : intake.risk_level === "Medium" ? "#D97706" : "#16A34A"}>
                              Risk: {intake.risk_level}
                            </Typography>
                          )}
                          {intake.submitted_by && (
                            <Chip 
                              label={`Submitter: ${intake.submitter ? `${intake.submitter.firstName || ''} ${intake.submitter.lastName || ''}`.trim() : intake.submitted_by} (${intake.submitted_by})`}
                              size="small"
                              sx={{ height: 20, fontSize: "0.68rem", bgcolor: "#F1F5F9", color: "#475569" }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      {getPriorityChip(intake.priority)}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="#0F172A">
                        {companyConfig?.currency_symbol || '₹'}{parseFloat(intake.estimated_budget || 0).toLocaleString()}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="#64748B" display="block">
                        Start: {intake.proposed_start_date || "TBD"}
                      </Typography>
                      <Typography variant="caption" color="#64748B" display="block">
                        End: {intake.proposed_end_date || "TBD"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ minWidth: 150 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {/* Strategic Bar */}
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.25 }}>
                            <Typography variant="caption" color="#64748B" fontWeight={600} sx={{ fontSize: "0.7rem" }}>
                              Strategic
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color="#4F46E5" sx={{ fontSize: "0.72rem" }}>
                              {intake.strategic_alignment_score || 5}/10 ({(intake.strategic_alignment_score || 5) * 10}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(intake.strategic_alignment_score || 5) * 10}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: "#EEF2FF",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 3,
                                background: "linear-gradient(90deg, #818CF8, #4F46E5)"
                              }
                            }}
                          />
                        </Box>

                        {/* Feasibility Bar */}
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.25 }}>
                            <Typography variant="caption" color="#64748B" fontWeight={600} sx={{ fontSize: "0.7rem" }}>
                              Feasibility
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color="#059669" sx={{ fontSize: "0.72rem" }}>
                              {intake.feasibility_score || 5}/10 ({(intake.feasibility_score || 5) * 10}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={(intake.feasibility_score || 5) * 10}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: "#ECFDF5",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 3,
                                background: "linear-gradient(90deg, #34D399, #059669)"
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ minWidth: 160 }}>
                      <Box>
                        {getStatusChip(intake.status)}
                        {intake.status_change_reason && (
                          <Tooltip title={`Status Justification: ${intake.status_change_reason}`} arrow placement="top">
                            <Typography
                              variant="caption"
                              color="#475569"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                mt: 0.6,
                                fontSize: "0.72rem",
                                lineHeight: 1.25,
                                fontStyle: "italic",
                                cursor: "pointer",
                                bgcolor: "#F1F5F9",
                                px: 0.75,
                                py: 0.35,
                                borderRadius: 1,
                                borderLeft: "2px solid #94A3B8"
                              }}
                            >
                              "{intake.status_change_reason}"
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        {/* AI Recommendation Button for Feasibility Check */}
                        {intake.status === "Feasibility_Check" && (
                          <Tooltip title="AI Resource & Feasibility Recommendation">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<Sparkles size={14} style={{ color: "#ffffff" }} />}
                              onClick={() => handleTriggerIndividualAI(intake.intake_id, intake.title)}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                bgcolor: "#7C3AED",
                                "&:hover": { bgcolor: "#6D28D9" },
                                borderRadius: 1.5,
                                px: 1.5,
                                fontWeight: 600,
                                boxShadow: "0 2px 8px rgba(124, 58, 237, 0.25)"
                              }}
                            >
                              AI Recommendation
                            </Button>
                          </Tooltip>
                        )}

                        {/* Convert to Project Button */}
                        {intake.status === "Approved" && (
                          <Tooltip title="Convert to Active Project">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<Rocket size={14} />}
                              onClick={() => handleOpenConvertModal(intake)}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                bgcolor: "#16A34A",
                                "&:hover": { bgcolor: "#15803D" },
                                borderRadius: 1.5,
                                px: 1.5
                              }}
                            >
                              Launch Project
                            </Button>
                          </Tooltip>
                        )}

                        {/* If already converted, view project */}
                        {intake.status === "Converted" && (
                          <Tooltip title="View Project in Portfolio">
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ArrowUpRight size={14} />}
                              onClick={() => navigate("/cpo-ppm-dashboard")}
                              sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: 1.5 }}
                            >
                              View Project
                            </Button>
                          </Tooltip>
                        )}

                        {/* Edit Proposal Button */}
                        <Tooltip title="Edit Proposal & Evaluation">
                          <IconButton size="small" onClick={() => handleOpenEditModal(intake)} sx={{ color: "#4F46E5" }}>
                            <Edit3 size={16} />
                          </IconButton>
                        </Tooltip>

                        {/* View Details Button */}
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => { setSelectedIntake(intake); setDetailsModalOpen(true); }} sx={{ color: "#64748B" }}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>

                        {/* Delete (if not converted) */}
                        {intake.status !== "Converted" && (
                          <Tooltip title="Delete Intake">
                            <IconButton size="small" onClick={() => handleDeleteIntake(intake.intake_id)} sx={{ color: "#EF4444" }}>
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ========================================================================= */}
      {/* MODAL 1: SUBMIT NEW INTAKE PROPOSAL                                        */}
      {/* ========================================================================= */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #E2E8F0", pb: 2 }}>
          Submit New Project Intake Proposal
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Proposal / Initiative Title *"
                placeholder="e.g. AI-Powered Resume Scoring Engine"
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={createForm.category}
                  label="Category"
                  onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                >
                  <MenuItem value="Software">Software</MenuItem>
                  <MenuItem value="Hardware">Hardware</MenuItem>
                  <MenuItem value="Product Feature">Product Feature</MenuItem>
                  <MenuItem value="Client Solution">Client Solution</MenuItem>
                  <MenuItem value="Internal Tool">Internal Tool</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Submitted By (Employee ID)"
                value={currentEmployeeId ? `${currentEmpName} (${currentEmployeeId})` : "Fetching Employee ID..."}
                InputProps={{ readOnly: true }}
                variant="filled"
                helperText="Auto-captured logged-in employee session"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Business Case & Justification"
                placeholder="Explain the problem statement, business value, target ROI, and strategic goals..."
                value={createForm.business_case}
                onChange={(e) => setCreateForm({ ...createForm, business_case: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={`Estimated Budget (${companyConfig?.currency_symbol || '₹'})`}
                type="number"
                value={createForm.estimated_budget}
                onChange={(e) => setCreateForm({ ...createForm, estimated_budget: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="Proposed Start Date"
                InputLabelProps={{ shrink: true }}
                value={createForm.proposed_start_date}
                onChange={(e) => setCreateForm({ ...createForm, proposed_start_date: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="Proposed End Date"
                InputLabelProps={{ shrink: true }}
                value={createForm.proposed_end_date}
                onChange={(e) => setCreateForm({ ...createForm, proposed_end_date: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={createForm.priority}
                  label="Priority"
                  onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Risk Rating</InputLabel>
                <Select
                  value={createForm.risk_level}
                  label="Risk Rating"
                  onChange={(e) => setCreateForm({ ...createForm, risk_level: e.target.value })}
                >
                  <MenuItem value="Low">Low Risk</MenuItem>
                  <MenuItem value="Medium">Medium Risk</MenuItem>
                  <MenuItem value="High">High Risk</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Strategic & Technical Progress Bars (Interactive inside Create Form) */}
            <Grid item xs={12}>
              {renderScoreMeters(createForm, setCreateForm)}
            </Grid>

            {/* Requested Skill Requirements */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#334155">
                  Estimated Resource & Skill Requirements
                </Typography>
                <Button size="small" startIcon={<Plus size={14} />} onClick={handleAddSkillRow} sx={{ textTransform: "none" }}>
                  Add Role
                </Button>
              </Box>

              {createForm.requested_skills.map((item, idx) => (
                <Grid container spacing={1.5} key={idx} sx={{ mb: 1.5 }} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Role (e.g. Frontend Dev)"
                      value={item.role}
                      onChange={(e) => handleSkillChange(idx, "role", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      placeholder="Count"
                      value={item.count}
                      onChange={(e) => handleSkillChange(idx, "count", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Skills (e.g. React, Redux)"
                      value={item.skill}
                      onChange={(e) => handleSkillChange(idx, "skill", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton size="small" onClick={() => handleRemoveSkillRow(idx)} disabled={createForm.requested_skills.length === 1}>
                      <Trash2 size={16} color="#EF4444" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: "1px solid #E2E8F0" }}>
          <Button onClick={() => setCreateModalOpen(false)} sx={{ textTransform: "none", color: "#64748B" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitCreate}
            disabled={actionLoading}
            sx={{ textTransform: "none", bgcolor: "#4F46E5", "&:hover": { bgcolor: "#4338CA" } }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Submit Proposal"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* MODAL 2: FULL EDIT INTAKE PROPOSAL MODAL                                  */}
      {/* ========================================================================= */}
      <Dialog 
        open={editModalOpen || reviewModalOpen} 
        onClose={() => { setEditModalOpen(false); setReviewModalOpen(false); }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Edit3 size={20} color="#4F46E5" />
            <Typography variant="h6" fontWeight={700} color="#0F172A">
              Edit Intake Proposal: {selectedIntake?.title}
            </Typography>
          </Box>
          {selectedIntake && getStatusChip(selectedIntake.status)}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            {/* Title & Category */}
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Proposal / Initiative Title *"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editForm.category}
                  label="Category"
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                >
                  <MenuItem value="Software">Software</MenuItem>
                  <MenuItem value="Hardware">Hardware</MenuItem>
                  <MenuItem value="Product Feature">Product Feature</MenuItem>
                  <MenuItem value="Client Solution">Client Solution</MenuItem>
                  <MenuItem value="Internal Tool">Internal Tool</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Business Case */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Business Case & Justification"
                value={editForm.business_case}
                onChange={(e) => setEditForm({ ...editForm, business_case: e.target.value })}
              />
            </Grid>

            {/* Financial & Dates */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={`Estimated Budget (${companyConfig?.currency_symbol || '₹'})`}
                type="number"
                value={editForm.estimated_budget}
                onChange={(e) => setEditForm({ ...editForm, estimated_budget: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="Proposed Start Date"
                InputLabelProps={{ shrink: true }}
                value={editForm.proposed_start_date}
                onChange={(e) => setEditForm({ ...editForm, proposed_start_date: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="Proposed End Date"
                InputLabelProps={{ shrink: true }}
                value={editForm.proposed_end_date}
                onChange={(e) => setEditForm({ ...editForm, proposed_end_date: e.target.value })}
              />
            </Grid>

            {/* Priority & Risk */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={editForm.priority}
                  label="Priority"
                  onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Risk Rating</InputLabel>
                <Select
                  value={editForm.risk_level}
                  label="Risk Rating"
                  onChange={(e) => setEditForm({ ...editForm, risk_level: e.target.value })}
                >
                  <MenuItem value="Low">Low Risk</MenuItem>
                  <MenuItem value="Medium">Medium Risk</MenuItem>
                  <MenuItem value="High">High Risk</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Strategic & Technical Progress Bars in Form */}
            <Grid item xs={12}>
              {renderScoreMeters(editForm, setEditForm)}
            </Grid>

            {/* Requested Skill Requirements */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#334155">
                  Resource & Skill Requirements
                </Typography>
                <Button size="small" startIcon={<Plus size={14} />} onClick={handleEditAddSkillRow} sx={{ textTransform: "none" }}>
                  Add Role
                </Button>
              </Box>

              {editForm.requested_skills.map((item, idx) => (
                <Grid container spacing={1.5} key={idx} sx={{ mb: 1.5 }} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Role (e.g. Frontend Dev)"
                      value={item.role}
                      onChange={(e) => handleEditSkillChange(idx, "role", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      placeholder="Count"
                      value={item.count}
                      onChange={(e) => handleEditSkillChange(idx, "count", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Skills (e.g. React, Redux)"
                      value={item.skill}
                      onChange={(e) => handleEditSkillChange(idx, "skill", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton size="small" onClick={() => handleEditRemoveSkillRow(idx)} disabled={editForm.requested_skills.length === 1}>
                      <Trash2 size={16} color="#EF4444" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            {/* Stage / Status Management & Mandatory Status Change Reason */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} color="#1E293B" sx={{ mb: 1.5 }}>
                Stage & Decision Status Management
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Proposal Stage / Status</InputLabel>
                    <Select
                      value={editForm.status}
                      label="Proposal Stage / Status"
                      onChange={(e) => {
                        setEditForm({ ...editForm, status: e.target.value });
                        if (e.target.value === selectedIntake?.status) {
                          setStatusReasonError("");
                        }
                      }}
                    >
                      <MenuItem value="Submitted">Submitted</MenuItem>
                      <MenuItem value="Under_Review">Under Review</MenuItem>
                      <MenuItem value="Feasibility_Check">Feasibility Check</MenuItem>
                      <MenuItem value="Approved">Approved (Ready for Conversion)</MenuItem>
                      <MenuItem value="On_Hold">Put On Hold</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status Transition Detection Alert */}
                {editForm.status !== selectedIntake?.status && (
                  <Grid item xs={12}>
                    <Alert severity="warning" icon={<AlertTriangle size={18} />} sx={{ borderRadius: 2 }}>
                      <b>Status Transition Detected:</b> Changing stage from <b>{selectedIntake?.status?.replace("_", " ")}</b> to <b>{editForm.status?.replace("_", " ")}</b>.
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                        A mandatory reason must be provided below to explain this status change.
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {/* Mandatory Status Change Reason Field */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required={editForm.status !== selectedIntake?.status}
                    error={Boolean(statusReasonError)}
                    helperText={
                      statusReasonError ||
                      (editForm.status !== selectedIntake?.status
                        ? "Mandatory: State the justification for changing the proposal status."
                        : "Previous or optional reason note.")
                    }
                    multiline
                    rows={2}
                    label={
                      editForm.status !== selectedIntake?.status
                        ? "Reason for Status Update * (Mandatory)"
                        : "Status Change Reason"
                    }
                    placeholder="Enter the justification / rationale for updating the status..."
                    value={editForm.status_change_reason}
                    onChange={(e) => {
                      setEditForm({ ...editForm, status_change_reason: e.target.value });
                      if (e.target.value.trim()) setStatusReasonError("");
                    }}
                    sx={{
                      bgcolor: editForm.status !== selectedIntake?.status ? "#FFFBEB" : "#F8FAFC",
                      "& .MuiOutlinedInput-root": {
                        borderColor: editForm.status !== selectedIntake?.status ? "#F59E0B" : undefined
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Reviewer Notes & Feedback"
                    placeholder="Add evaluation comments, feasibility findings, or next steps..."
                    value={editForm.review_notes}
                    onChange={(e) => setEditForm({ ...editForm, review_notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: "1px solid #E2E8F0" }}>
          <Button 
            onClick={() => { setEditModalOpen(false); setReviewModalOpen(false); }} 
            sx={{ textTransform: "none", color: "#64748B" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitEdit}
            disabled={actionLoading}
            sx={{ textTransform: "none", bgcolor: "#4F46E5", "&:hover": { bgcolor: "#4338CA" } }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Save All Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* MODAL 3: CONVERT TO ACTIVE PROJECT WIZARD                                 */}
      {/* ========================================================================= */}
      <Dialog open={convertModalOpen} onClose={() => setConvertModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 1 }}>
          <Rocket size={20} color="#16A34A" /> Convert NPI Intake to Active Project
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert
            severity="info"
            action={
              <Button
                color="primary"
                variant="outlined"
                size="small"
                endIcon={<ArrowUpRight size={14} />}
                onClick={() => {
                  setConvertModalOpen(false);
                  navigate('/create-project', { state: { npiData: selectedIntake } });
                }}
                sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#EFF6FF" }}
              >
                Redirect to Create Project Tab
              </Button>
            }
            sx={{ mb: 2.5, borderRadius: 2, display: "flex", alignItems: "center" }}
          >
            Converting this approved intake will create an active project. Logged in as <strong>{isProductOwner ? "Product Owner" : isProjectManager ? "Project Manager" : isProductManager ? "Product Manager" : (userRole || "User")}</strong> ({currentEmpName}).
          </Alert>

          <Grid container spacing={2.5}>
            {/* Project Basic Info */}
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Active Project Name *"
                value={convertForm.name}
                onChange={(e) => setConvertForm({ ...convertForm, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Project Key (Unique) *"
                placeholder="e.g. AI-101"
                value={convertForm.key}
                onChange={(e) => setConvertForm({ ...convertForm, key: e.target.value.toUpperCase() })}
                helperText="Identifier for tickets & sprints"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Template</InputLabel>
                <Select
                  value={convertForm.template}
                  label="Template"
                  onChange={(e) => setConvertForm({ ...convertForm, template: e.target.value })}
                >
                  <MenuItem value="Scrum">Scrum (Sprint-based)</MenuItem>
                  <MenuItem value="Kanban">Kanban (Continuous flow)</MenuItem>
                  <MenuItem value="Bug Tracking">Bug Tracking</MenuItem>
                  <MenuItem value="Waterfall">Waterfall</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Project Type</InputLabel>
                <Select
                  value={convertForm.projectType}
                  label="Project Type"
                  onChange={(e) => setConvertForm({ ...convertForm, projectType: e.target.value })}
                >
                  <MenuItem value="Software">Software</MenuItem>
                  <MenuItem value="Product">Product</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="IT">IT</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="Client Solution">Client Solution</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Leadership Assignments */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Assign Project Leadership (Role-Filtered)" size="small" sx={{ fontWeight: 600 }} />
              </Divider>
            </Grid>

            {/* Project Manager Field */}
            <Grid item xs={12} sm={6}>
              {isProjectManager ? (
                <TextField
                  fullWidth
                  label="Project Manager *"
                  value={`${currentEmpName} (Auto-assigned)`}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  helperText="Auto-assigned to you (Logged-in Project Manager)"
                />
              ) : (
                <FormControl fullWidth required>
                  <InputLabel id="convert-pm-label">Project Manager *</InputLabel>
                  <Select
                    labelId="convert-pm-label"
                    value={convertForm.manager_id}
                    label="Project Manager *"
                    onChange={(e) => setConvertForm({ ...convertForm, manager_id: e.target.value })}
                  >
                    <MenuItem value="">-- Select Project Manager --</MenuItem>
                    {(projectManagersList.length > 0 ? projectManagersList : employees).map((emp) => (
                      <MenuItem key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                        {emp.firstName} {emp.lastName} ({getEmployeeRole(emp) || 'Project Manager'})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select designated Project Manager</FormHelperText>
                </FormControl>
              )}
            </Grid>

            {/* Product Manager Field */}
            <Grid item xs={12} sm={6}>
              {isProductManager ? (
                <TextField
                  fullWidth
                  label="Product Manager"
                  value={`${currentEmpName} (Auto-assigned)`}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  helperText="Auto-assigned to you (Logged-in Product Manager)"
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="convert-prodm-label">Product Manager</InputLabel>
                  <Select
                    labelId="convert-prodm-label"
                    value={convertForm.product_manager_id}
                    label="Product Manager"
                    onChange={(e) => setConvertForm({ ...convertForm, product_manager_id: e.target.value })}
                  >
                    <MenuItem value="">-- Unassigned --</MenuItem>
                    {(productManagersList.length > 0 ? productManagersList : employees).map((emp) => (
                      <MenuItem key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                        {emp.firstName} {emp.lastName} ({getEmployeeRole(emp) || 'Product Manager'})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select designated Product Manager</FormHelperText>
                </FormControl>
              )}
            </Grid>

            {/* Product Owner Field */}
            <Grid item xs={12} sm={6}>
              {isProductOwner ? (
                <TextField
                  fullWidth
                  label="Product Owner"
                  value={`${currentEmpName} (Auto-assigned)`}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  helperText="Auto-assigned to you (Logged-in Product Owner)"
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="convert-po-label">Product Owner</InputLabel>
                  <Select
                    labelId="convert-po-label"
                    value={convertForm.product_owner_id}
                    label="Product Owner"
                    onChange={(e) => setConvertForm({ ...convertForm, product_owner_id: e.target.value })}
                  >
                    <MenuItem value="">-- Unassigned --</MenuItem>
                    {(productOwnersList.length > 0 ? productOwnersList : employees).map((emp) => (
                      <MenuItem key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                        {emp.firstName} {emp.lastName} ({getEmployeeRole(emp) || 'Product Owner'})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select designated Product Owner</FormHelperText>
                </FormControl>
              )}
            </Grid>

            {/* Tech Lead */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="convert-lead-label">Tech Lead</InputLabel>
                <Select
                  labelId="convert-lead-label"
                  value={convertForm.lead_id}
                  label="Tech Lead"
                  onChange={(e) => setConvertForm({ ...convertForm, lead_id: e.target.value })}
                >
                  <MenuItem value="">-- Unassigned --</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                      {emp.firstName} {emp.lastName} ({emp.employee_id || emp.id})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select designated Technical / Engineering Lead</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Cost Center (Finance)</InputLabel>
                <Select
                  value={convertForm.costCenterId}
                  label="Cost Center (Finance)"
                  onChange={(e) => setConvertForm({ ...convertForm, costCenterId: e.target.value })}
                >
                  <MenuItem value="">-- Select Cost Center --</MenuItem>
                  {costCenters.map((cc) => (
                    <MenuItem key={cc.id} value={cc.id}>
                      {cc.name} ({cc.code || `ID: ${cc.id}`})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`Budget (${companyConfig?.currency_symbol || '₹'})`}
                type="number"
                value={convertForm.budget}
                onChange={(e) => setConvertForm({ ...convertForm, budget: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={convertForm.start_date}
                onChange={(e) => setConvertForm({ ...convertForm, start_date: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={convertForm.end_date}
                onChange={(e) => setConvertForm({ ...convertForm, end_date: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Project Description / Goals"
                value={convertForm.description}
                onChange={(e) => setConvertForm({ ...convertForm, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowUpRight size={16} />}
            onClick={() => {
              setConvertModalOpen(false);
              navigate('/create-project', { state: { npiData: selectedIntake } });
            }}
            sx={{ textTransform: "none", fontWeight: 600, color: "#2563EB", borderColor: "#93C5FD", "&:hover": { bgcolor: "#EFF6FF", borderColor: "#2563EB" } }}
          >
            Redirect to Create Project Tab
          </Button>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button onClick={() => setConvertModalOpen(false)} sx={{ textTransform: "none", color: "#64748B" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Rocket size={16} />}
              onClick={handleSubmitConvert}
              disabled={actionLoading}
              sx={{ textTransform: "none", bgcolor: "#16A34A", "&:hover": { bgcolor: "#15803D" } }}
            >
              {actionLoading ? <CircularProgress size={20} color="inherit" /> : "Confirm & Launch Project"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* MODAL 4: INTAKE PROPOSAL DETAILS DRAWER/DIALOG                            */}
      {/* ========================================================================= */}
      <Dialog open={detailsModalOpen} onClose={handleCloseDetailsModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "1px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{selectedIntake?.title}</span>
          {selectedIntake && getStatusChip(selectedIntake.status)}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedIntake && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" color="#64748B" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>
                  Business Case & Problem Statement
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 1,
                    p: 2,
                    bgcolor: "#F8FAFC",
                    borderColor: "#E2E8F0",
                    borderRadius: 2
                  }}
                >
                  <Stack spacing={1.25}>
                    {formatBusinessCaseToBullets(selectedIntake.business_case).map((bullet, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: "#4F46E5",
                            mt: "7px",
                            flexShrink: 0
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="#1E293B"
                          sx={{
                            lineHeight: 1.6,
                            fontSize: "0.875rem",
                            fontWeight: 500
                          }}
                        >
                          {bullet}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="#64748B">Category</Typography>
                  <Typography variant="body2" fontWeight={600}>{selectedIntake.category}</Typography>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="#64748B">Priority</Typography>
                  <Typography variant="body2" fontWeight={600}>{selectedIntake.priority}</Typography>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="#64748B">Estimated Budget</Typography>
                  <Typography variant="body2" fontWeight={600}>{companyConfig?.currency_symbol || '₹'}{parseFloat(selectedIntake.estimated_budget || 0).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="#64748B">Risk Level</Typography>
                  <Typography variant="body2" fontWeight={600}>{selectedIntake.risk_level || "Low"}</Typography>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Typography variant="caption" color="#64748B">Submitted By</Typography>
                  <Typography variant="body2" fontWeight={600} color="#0F172A">
                    {selectedIntake.submitter ? `${selectedIntake.submitter.firstName || ''} ${selectedIntake.submitter.lastName || ''}`.trim() : (selectedIntake.submitted_by || "Not recorded")}
                    {selectedIntake.submitted_by && ` (${selectedIntake.submitted_by})`}
                  </Typography>
                </Grid>
              </Grid>

              {/* Strategic & Technical Evaluation Meters in Details */}
              <Box sx={{ p: 2, bgcolor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 2 }}>
                <Typography variant="caption" color="#64748B" fontWeight={700} textTransform="uppercase" sx={{ mb: 1.5, display: "block" }}>
                  Strategic & Technical Feasibility Scores
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: "#FFFFFF", borderRadius: 2, border: "1px solid #E2E8F0" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} color="#1E293B">Strategic Alignment</Typography>
                        <Typography variant="body2" fontWeight={700} color="#4F46E5">
                          {selectedIntake.strategic_alignment_score || 5}/10 ({(selectedIntake.strategic_alignment_score || 5) * 10}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(selectedIntake.strategic_alignment_score || 5) * 10}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "#EEF2FF",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            background: "linear-gradient(90deg, #818CF8, #4F46E5)"
                          }
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: "#FFFFFF", borderRadius: 2, border: "1px solid #E2E8F0" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} color="#1E293B">Technical Feasibility</Typography>
                        <Typography variant="body2" fontWeight={700} color="#059669">
                          {selectedIntake.feasibility_score || 5}/10 ({(selectedIntake.feasibility_score || 5) * 10}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(selectedIntake.feasibility_score || 5) * 10}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "#ECFDF5",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 4,
                            background: "linear-gradient(90deg, #34D399, #059669)"
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Status Change Reason */}
              {selectedIntake.status_change_reason && (
                <Box>
                  <Typography variant="caption" color="#B45309" fontWeight={700} textTransform="uppercase">Reason for Status Update</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, p: 1.5, bgcolor: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 1.5, color: "#92400E", fontWeight: 600 }}>
                    {selectedIntake.status_change_reason}
                  </Typography>
                </Box>
              )}

              {selectedIntake.requested_skills && (
                <Box>
                  <Typography variant="caption" color="#64748B" fontWeight={600} textTransform="uppercase">Requested Resource Demands</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 0.5, borderRadius: 2 }}>
                    {(() => {
                      try {
                        const parsed = JSON.parse(selectedIntake.requested_skills);
                        return Array.isArray(parsed) ? (
                          <Stack spacing={1}>
                            {parsed.map((item, i) => (
                              <Box key={i} sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" fontWeight={600}>{item.role} ({item.count} FTE)</Typography>
                                <Typography variant="caption" color="#64748B">{item.skill}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        ) : <Typography variant="body2">{selectedIntake.requested_skills}</Typography>;
                      } catch {
                        return <Typography variant="body2">{selectedIntake.requested_skills}</Typography>;
                      }
                    })()}
                  </Paper>
                </Box>
              )}

              {selectedIntake.review_notes && (
                <Box>
                  <Typography variant="caption" color="#64748B" fontWeight={600} textTransform="uppercase">Review Notes</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, p: 1.5, bgcolor: "#EEF2FF", borderRadius: 1.5, color: "#3730A3" }}>
                    {selectedIntake.review_notes}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #E2E8F0", justifyContent: "space-between" }}>
          <Box>
            {selectedIntake?.status === "Feasibility_Check" && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Sparkles size={14} style={{ color: "#ffffff" }} />}
                onClick={() => {
                  handleCloseDetailsModal();
                  handleTriggerIndividualAI(selectedIntake.intake_id, selectedIntake.title);
                }}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  bgcolor: "#7C3AED",
                  "&:hover": { bgcolor: "#6D28D9" },
                  borderRadius: 1.5,
                  px: 2,
                  fontWeight: 600,
                  boxShadow: "0 2px 8px rgba(124, 58, 237, 0.25)"
                }}
              >
                AI Recommendation
              </Button>
            )}
          </Box>
          <Button onClick={handleCloseDetailsModal} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* MODAL 5: INDIVIDUAL NPI AI RESOURCE & FEASIBILITY RECOMMENDATIONS MODAL    */}
      {/* ========================================================================= */}
      <Dialog
        open={individualModalOpen}
        onClose={() => setIndividualModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8fafc", p: 2.5 }}>
          <Box display="flex" alignItems="center">
            <Sparkles size={20} style={{ color: "#7C3AED", marginRight: "8px" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#4C1D95" }}>
              AI Resource Recommendations: {npiAiTitle}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setIndividualModalOpen(false)}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: "#ffffff" }}>
          {individualAiError && (
            <Alert
              severity={individualAiError.severity || "error"}
              sx={{ mb: 2, borderRadius: "10px", alignItems: "center" }}
              action={
                individualAiError.type === "upgrade" && (
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={() => window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI NPI Resource Matching" } }))}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Upgrade Plan
                  </Button>
                )
              }
            >
              {individualAiError.message}
            </Alert>
          )}
          {individualAiLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6}>
              <CircularProgress size={40} style={{ color: "#7C3AED" }} />
              <Typography variant="body2" sx={{ mt: 2, color: "#64748b", fontWeight: 500 }}>
                AI Resource Planner is calculating candidate matchings & feasibility...
              </Typography>
            </Box>
          ) : individualAiData ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#7C3AED", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Strategic Fit & Budget Check
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mb: 3, lineHeight: 1.6 }}>
                {individualAiData.strategic_fit_analysis}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#7C3AED", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Resource Readiness Summary
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mb: 3, lineHeight: 1.6 }}>
                {individualAiData.resource_readiness_summary}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#7C3AED", mb: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Recommended Resource Allocations
              </Typography>
              
              {individualAiData.recommendations?.map((roleGroup, groupIdx) => (
                <Box key={groupIdx} sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", borderBottom: "2px solid #f1f5f9", pb: 1, mb: 2 }}>
                    Role: {roleGroup.role} <span style={{ fontWeight: 500, color: "#64748b", fontSize: "0.85rem" }}>(Requested: {roleGroup.requested_count})</span>
                  </Typography>

                  {roleGroup.recommended_employees?.length > 0 ? (
                    <Grid container spacing={2.5}>
                      {roleGroup.recommended_employees.map((rec, recIdx) => (
                        <Grid item xs={12} sm={6} key={recIdx}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2.5,
                              borderRadius: "12px",
                              borderColor: "#e2e8f0",
                              transition: "all 0.2s",
                              "&:hover": { borderColor: "#7C3AED", boxShadow: "0 4px 12px rgba(124,58,237,0.08)" }
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                  {rec.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#64748b" }}>
                                  ID: {rec.employee_id}
                                </Typography>
                              </Box>
                              <Chip
                                label={`${rec.match_percentage}% Match`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  bgcolor: rec.match_percentage >= 85 ? "#dcfce7" : rec.match_percentage >= 60 ? "#fef3c7" : "#fee2e2",
                                  color: rec.match_percentage >= 85 ? "#15803d" : rec.match_percentage >= 60 ? "#b45309" : "#b91c1c"
                                }}
                              />
                            </Box>

                            <Box mb={1.5}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                                Matching Skills
                              </Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                {rec.matching_skills?.map((skill, sIdx) => (
                                  <Chip
                                    key={sIdx}
                                    label={skill}
                                    size="small"
                                    sx={{ fontSize: "0.7rem", height: 20, bgcolor: "#f1f5f9", color: "#334155" }}
                                  />
                                ))}
                              </Box>
                            </Box>

                            <Box mb={1.5}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                                Allocation / KT Recommendation
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: rec.allocation_suggestion?.toLowerCase().includes("kt") || rec.allocation_suggestion?.toLowerCase().includes("knowledge") ? "#b45309" : "#10b981", mt: 0.5 }}>
                                {rec.allocation_suggestion}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                                Fit Rationale
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#475569", mt: 0.5, fontSize: "0.8rem", lineHeight: 1.4 }}>
                                {rec.match_reason}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" sx={{ color: "#dc2626", fontStyle: "italic" }}>
                      ⚠️ No suitable candidates found for this role.
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              No matching recommendations available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setIndividualModalOpen(false)}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600, bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NpiIntakeDashboard;
